<template>
  <base-page-template>
    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        データ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-container fluid>
      <data-profile-info
          class="other-data-profile"
          :data-profile-view="otherDataProfile"
          :loading="otherDataProfileLoading"
          hide-service-type
      >
        <template #title>取引先データプロファイル</template>
        <template #subtitle>
          <template>取引先が</template>
          <template v-if="['common', 'consume'].includes(otherDataProfile.serviceType)">利用</template>
          <template v-if="['common', 'produce'].includes(otherDataProfile.serviceType)">提供</template>
          <template>するデータ項目プロファイルの一覧です。</template>
        </template>
        <template #pull-request-parameter>
          <!-- PR パラメータ発行側 -->
          <template v-if="tradeContract.amiIssuer">
            <v-col cols="3">
              <v-switch
                  :label="tradeContract.sendType === 'pull' ? 'PULL リクエスト可' : 'PULL リクエスト不可'"
                  v-model="tradeContract.sendType"
                  true-value="pull"
                  false-value="push"
                  :readonly="!editable || tradeContract.isSubscribeMode || tradeContract.isPublishMode"
                  hide-details>
              </v-switch>
            </v-col>
            <v-col cols="3">
              <text-field-like
                  v-if="tradeContract.sendType === 'pull'"
                  label="PULL リクエストパラメータの指定方法"
                  :value="parameterDescription"
                  :loading="parameterDescriptionLoading"
                  placeholder="未設定"
                  hide-details
                  multi-line>
              </text-field-like>
            </v-col>
          </template>

          <!-- PR パラメータ受領側 -->
          <template v-else>
            <v-col cols="3">
              <text-field-like
                  label="PUSH/PULL"
                  :value="tradeContract.sendType === 'pull' ? 'PULL リクエスト可' : 'PULL リクエスト不可'"
                  hide-details>
              </text-field-like>
            </v-col>
            <v-col cols="3">
              <text-field-like
                  v-if="tradeContract.sendType === 'pull'"
                  label="PULL リクエストパラメータの指定方法"
                  :value="parameterDescription"
                  :loading="parameterDescriptionLoading"
                  placeholder="未設定"
                  hide-details
                  multi-line>
              </text-field-like>
            </v-col>
          </template>
        </template>
        <template #append>
          <v-divider class="mt-3"></v-divider>

          <v-skeleton-loader
              type="table-thead, table-tbody"
              max-height="200"
              v-if="otherDataPropertyProfilesLoading">
          </v-skeleton-loader>

          <!-- 起案側データ項目プロパティーテーブル -->
          <el-table
              :data="otherDataPropertyProfiles"
              :row-class-name="({row}) => row.connector.errors().hasError ? 'highlight-row' : ''"
              border
              v-else>
            <el-table-column
                label="追番"
                prop="index"
                width="70"
                align="center">
            </el-table-column>
            <el-table-column
                label="名称"
                width="200"
                prop="name">
              <template #header>
                <div style="display: flex;">
                  名称
                  <v-spacer></v-spacer>
                  <icon-btn
                      :tooltip="`${dictionaryTypeName(otherDataPropertyProfileNameIs)}を表示中`"
                      tooltip-placement="top"
                      tooltip-no-delay
                      icon="mdi-reload"
                      :icon-color="dictionaryTypeColor(otherDataPropertyProfileNameIs)"
                      @click="otherDataPropertyProfileNameIs = otherDataPropertyProfileNameIs === 'common' ? 'local' : 'common'"
                      x-small>
                  </icon-btn>
                </div>
              </template>
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="otherDataPropertyProfileName(row)"
                      :placeholder="row.localName">
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
            <el-table-column
                label="説明"
                prop="description">
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="otherDataPropertyProfileDescription(row)"
                      placeholder="未設定"
                      multi-line>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
            <el-table-column
                label="データ型"
                prop="dataType"
                width="120">
              <template #default="{row}">
                {{ dataTypes[row.dataType] }}
              </template>
            </el-table-column>

            <el-table-column
                label="主キー"
                prop="isPrimaryKey"
                width="70"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row.isPrimaryKey"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>

            <el-table-column
                label="必須"
                prop="isRequired"
                width="70"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row.isRequired"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>

            <el-table-column
                label="省略値"
                width="200"
                prop="defaultValue">
            </el-table-column>

            <el-table-column
                label="対応"
                prop="isResponding"
                width="70"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <connector-error-indicator :connector="row.connector"></connector-error-indicator>
                </table-inline>
              </template>
            </el-table-column>
          </el-table>
        </template>
      </data-profile-info>
    </v-container>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        マッピング
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-container fluid>
      <v-card outlined class="elevation-0 self-data-profile">
        <v-card-title>
          自事業者データプロファイルのマッピング
          <v-spacer></v-spacer>
          <v-chip
              label dark color="gray darken-4" class="ml-3 px-4"
              v-if="['common', 'consume'].includes(selfDataProfile.serviceType)">
            利用
          </v-chip>
          <v-chip
              label dark color="gray darken-4" class="ml-3 px-4"
              v-if="['common', 'produce'].includes(selfDataProfile.serviceType)">
            提供
          </v-chip>
        </v-card-title>
        <v-card-subtitle>
          <template v-if="selfDataProfile.id">
            <template>自事業者が</template>
            <template v-if="['common', 'consume'].includes(selfDataProfile.serviceType)">利用</template>
            <template v-if="['common', 'produce'].includes(selfDataProfile.serviceType)">提供</template>
            <template>
              するデータ項目プロファイルの一覧です。
              取引先データとのデータ項目プロファイルとのマッピングを行ってください。
            </template>
          </template>
          <error-panel class="mt-2" :errors="errors" small></error-panel>
        </v-card-subtitle>
        <v-card-text>
          <v-row dense>
            <v-col cols="4">
              <text-field-like
                  v-if="selfDataProfilesLoading"
                  label="データプロファイル (データ構成モデル名)"
                  value=""
                  :value-id="tradeContract.selfDataProfileId"
                  loading>
              </text-field-like>
              <model-select
                  v-else
                  v-model="tradeContract.selfDataProfileId"
                  label="データプロファイル (データ構成モデル名)"
                  class="self-data-profile-select"
                  :items="selfDataProfilesView"
                  :groups="controllers"
                  item-group="controllerIds"
                  placeholder="選択してください"
                  :messages="tradeContract.selfDataProfileId"
                  @change="
                    clearLink();
                    resetServiceProfile();
                    $once('loaded-self-data-profile', () => { selfDataProfileIntoView() });
                  "
                  :readonly="!editable"
                  default-expand-all
                  taller
                  loose-menu-width
                  persistent-placeholder>
                <template #item="{item, attrs, on}">
                  <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover data-profile-select-item">
                    <v-col class="name">
                      <menu-inline>
                        <text-field-like
                            label="名称"
                            :value="item.name"
                            :value-id="item.id">
                        </text-field-like>
                      </menu-inline>
                    </v-col>
                    <v-col>
                      <menu-inline>
                        <text-field-like
                            label="説明"
                            :value="item.description"
                            placeholder="未設定"
                            multi-line>
                        </text-field-like>
                      </menu-inline>
                    </v-col>
                  </v-list-item>
                </template>
              </model-select>
            </v-col>
            <template v-if="tradeContract.selfDataProfileId">
              <v-col cols="3">
                <text-field-like
                    v-if="selfDataProfile && selfDataProfile.dataImplementation && selfDataProfile.dataImplementation.controller"
                    label="コントローラ名"
                    :value="selfDataProfile.dataImplementation.controller.name"
                    :loading="selfDataProfilesLoading"
                    hide-details>
                </text-field-like>
              </v-col>
              <v-col cols="2">
                <text-field-like
                    v-if="selfDataProfile && selfDataProfile.localDataComponentModel && selfDataProfile.localDataComponentModel.dictionary"
                    label="個別辞書名"
                    :value="selfDataProfile.localDataComponentModel.dictionary.name"
                    :value-hds-id="selfDataProfile.localDataComponentModel.dictionary.id"
                    :loading="selfDataProfilesLoading">
                  <template #default="{value}">
                    <deletable-field :value="value" :deleted="selfDataProfile.localDataComponentModel.dictionary.isDeleted"/>
                  </template>
                </text-field-like>
              </v-col>
              <v-col cols="2">
                <text-field-like
                    v-if="selfDataProfile && selfDataProfile.commonDataComponentModel && selfDataProfile.commonDataComponentModel.dictionary"
                    label="共通辞書名"
                    :value="selfDataProfile.commonDataComponentModel.dictionary.name"
                    :value-hds-id="selfDataProfile.commonDataComponentModel.dictionary.id"
                    :loading="selfDataProfilesLoading">
                  <template #default="{value}">
                    <deletable-field :value="value" :deleted="selfDataProfile.commonDataComponentModel.dictionary.isDeleted"/>
                  </template>
                </text-field-like>
              </v-col>
            </template>
          </v-row>
          <v-row dense v-if="tradeContract.selfDataProfileId">
            <v-col cols="12">
              <text-field-like
                  label="説明"
                  :value="selfDataProfile.description"
                  :loading="selfDataProfilesLoading"
                  placeholder="未入力"
                  hide-details
                  multi-line>
              </text-field-like>
            </v-col>
          </v-row>
          <v-divider class="mt-3"></v-divider>

          <v-skeleton-loader
              type="table-thead, table-tbody"
              max-height="200"
              v-if="selfDataPropertyProfilesLoading">
          </v-skeleton-loader>

          <!-- 適応側データ項目プロパティーテーブル -->
          <el-table
              :data="selfDataPropertyProfiles"
              :row-class-name="({row}) => row.connector.errors().hasError ? 'highlight-row' : ''"
              border
              v-else>
            <el-table-column
                label="追番"
                prop="index"
                width="70"
                align="center">
            </el-table-column>
            <el-table-column
                label="名称"
                width="200"
                prop="name">
            </el-table-column>
            <el-table-column
                label="説明"
                prop="description">
              <template #default="{row}">
                <table-inline>
                  <text-field-like
                      :value="row.description"
                      placeholder="未設定"
                      multi-line>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>
            <el-table-column
                label="データ型"
                prop="dataType"
                width="120">
              <template #default="{row}">
                {{ dataTypes[row.dataType] }}
              </template>
            </el-table-column>

            <el-table-column
                label="主キー"
                prop="isPrimaryKey"
                width="70"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row.isPrimaryKey"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>

            <el-table-column
                label="必須"
                prop="isRequired"
                width="70"
                align="center">
              <template #default="{row}">
                <table-inline>
                  <readonly-checkbox :value="row.isRequired"></readonly-checkbox>
                </table-inline>
              </template>
            </el-table-column>

            <el-table-column
                label="省略値"
                width="200"
                align="center"
                column-key="defaultValue">
              <template #default="{row}">
                <table-inline>
                  <v-text-field
                      v-model="row.connector.defaultValue"
                      @change="tradeContract.markDirty()"
                      clearable
                      outlined
                      dense
                      v-if="editable">
                  </v-text-field>
                  <text-field-like
                      :value="row.connector.defaultValue"
                      v-else>
                  </text-field-like>
                </table-inline>
              </template>
            </el-table-column>

            <el-table-column
                label="取引先データとの対応"
                label-class-name="mapping"
                column-key="mapping"
                min-width="230"
                width="230"
                align="center">
              <template #header="{column}">
                <div>{{ column.label }}</div>
                <div class="subtitle">(追番 - 名称)</div>
                <el-tooltip content="自動マッピング" placement="top" :open-delay="500">
                  <v-btn icon @click="autoMapping">
                    <v-icon color="primary">mdi-swap-horizontal-bold</v-icon>
                  </v-btn>
                </el-tooltip>
              </template>
              <template #default="{row}">
                <table-inline>
                  <v-select
                      :value="getLinkBySelf(row.id, {}).otherDataPropertyProfileId"
                      @change="setLink(row.id, $event);"
                      :items="otherDataPropertyProfiles"
                      item-text="name"
                      item-value="id"
                      :clearable="editable"
                      :outlined="editable"
                      :readonly="!editable"
                      dense
                      hide-details>
                    <template #item="{item}">
                      <menu-inline>
                        <text-field-like :value="item.index"></text-field-like>
                      </menu-inline>
                      <span class="mx-1">-</span>
                      <menu-inline>
                        <text-field-like :value="otherDataPropertyProfileName(item)" :placeholder="item.localName"></text-field-like>
                      </menu-inline>
                    </template>
                    <template #selection="{item}">
                      <menu-inline>
                        <text-field-like :value="item.index"></text-field-like>
                      </menu-inline>
                      <span class="mx-1">-</span>
                      <menu-inline>
                        <text-field-like :value="otherDataPropertyProfileName(item)" :placeholder="item.localName"></text-field-like>
                      </menu-inline>
                    </template>
                    <template #append-outer>
                      <connector-error-indicator :connector="row.connector"></connector-error-indicator>
                    </template>
                  </v-select>
                </table-inline>
              </template>
            </el-table-column>
          </el-table>
        </v-card-text>
      </v-card>
    </v-container>

    <app-header :errors="errors">
      <error-panel :errors="errors" small></error-panel>
      <v-spacer></v-spacer>
      <v-btn
          elevation="0"
          color="primary"
          style="align-self: center;"
          :disabled="!tradeContract.dataPropertyProfileMapsIsReady"
          @click="$emit('next-step')">
        サービスの選択に進む
      </v-btn>
    </app-header>
  </base-page-template>
</template>

<script>
import DataPropertyProfileSource from "@/components/organisms/DataPropertyProfileSource";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import {DATA_PROPERTY_DEFINITION_DATA_TYPES, DICTIONARY_TYPE_COLORS, DICTIONARY_TYPE_NAMES} from "@/constants";
import TableInline from "@/components/atoms/TableInline.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import {mapActions, mapGetters, mapMutations} from "vuex";
import BetterSet from "betterset";
import gql from "graphql-tag";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import DataProfileSource from "@/components/organisms/DataProfileSource";
import DataPropertyProfileConnector from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileConnector";
import DataPropertyProfileLinkManager from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileLinkManager";
import natsort from "natsort";
import ConnectorErrorIndicator from "@/components/pages/contract/TradeContract/AdaptContract/ConnectorErrorIndicator.vue";
import {emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import DataProfileInfo from "@/components/organisms/DataProfileInfo.vue";
import IconBtn from "@/components/molecules/IconBtn.vue";
import DeletableField from "@/components/molecules/DeletableField.vue";
import ErrorPanel from "@/components/molecules/ErrorPanel.vue";
import scrollIntoView from "scroll-into-view-if-needed";
import MenuInline from "@/components/atoms/MenuInline.vue";
import AppHeader from "@/components/atoms/AppHeader.vue";
import Swal from "sweetalert2";

/** ソート関数 */
const sortFn = natsort();

export default {
  name: "DataProfile",
  components: {
    AppHeader,
    MenuInline,
    ErrorPanel,
    DeletableField,
    IconBtn,
    DataProfileInfo,
    ConnectorErrorIndicator,
    ModelSelect,
    BasePageTemplate,
    ReadonlyCheckbox, TableInline, TextFieldLike},
  mixins: [DataProfileSource, DataPropertyProfileSource],
  props: {
    tradeContracts: {
      type: Array,
      required: true
    },
    tradeContractIndex: {
      type: Number,
      default: 1
    },
    editable: {
      type: Boolean,
      required: true
    }
  },
  created() {
    this.load();
  },
  data() {
    return {
      controllers: [],
      selfDataProfiles: [],
      selfDataProfilesLoading: true,
      selfDataPropertyProfiles: [],
      selfDataPropertyProfilesLoading: true,
      otherDataProfile: {},
      otherDataProfileLoading: true,
      otherDataPropertyProfiles: [],
      otherDataPropertyProfileNameIs: 'common', // 'common' | 'local'
      otherDataPropertyProfilesLoading: true,
      dataTypes: DATA_PROPERTY_DEFINITION_DATA_TYPES,
      linkManager: new DataPropertyProfileLinkManager(() => this.tradeContract),
    }
  },
  computed: {
    ...mapGetters({
      hdsEntities: 'hds/entities',
      hcsClient: 'hcs/client',
    }),
    tradeContract() {
      return this.tradeContracts[this.tradeContractIndex - 1] ?? emptyTradeContractView();
    },
    selfDataProfile() {
      const dataProfile = this.selfDataProfiles.find(dp => dp.id === this.tradeContract.selfDataProfileId);
      const dataImplementation = dataProfile?.dataImplementation;
      return {
        ...dataProfile,
        dataImplementation: {
          ...dataImplementation,
          controller: this.controllers.find(c => c.id === dataImplementation?.controllerId) ?? {}
        }
      };
    },
    selfDataProfilesView() {
      const otherServiceType = this.otherDataProfile.serviceType;
      if(!otherServiceType) {
        return [];
      }
      const selfServiceType = (() => {
        switch(otherServiceType) {
          case 'consume': return 'produce';
          case 'produce': return 'consume';
          default: return 'common';
        }
      })();
      return this.selfDataProfiles.filter(sdp => sdp.serviceType === selfServiceType);
    },
    parameterDescription() {
      if(this.tradeContract.isSelfProduce) {
        return this.selfDataProfile?.dataImplementation?.parameterDescription;
      } else {
        return this.otherDataProfile?.dataImplementation?.parameterDescription;
      }
    },
    parameterDescriptionLoading() {
      if(this.tradeContract.isSelfProduce) {
        return this.selfDataProfileLoading;
      } else {
        return this.otherDataProfileLoading;
      }
    },
    errors() {
      const errors = [];
      if(!this.tradeContract.selfDataProfileId) {
        const onClick = () => {
          this.selfDataProfileIntoView();
          setTimeout(() => {
            const el = this.$el.querySelector('.self-data-profile-select input');
            el?.click();
          }, 500);
        };

        errors.push({
          level: 'info',
          message: [
              'はじめにマッピングさせるデータプロファイルを',
              this.$createElement('a', {
                on: {
                  click: onClick.bind(this)
                }
              }, ['選択']),
              'して下さい。'
          ]
        });
      } else if(!this.linkManager.hasLink) {
        const onClick = () => {
          this.selfDataProfileIntoView();
        };

        errors.push({
          level: 'info',
          message: [
            '必ず 1 つ以上のマッピングが必要です。',
            this.$createElement('a', {
              on: {
                click: onClick.bind(this)
              }
            }, ['取引先データとの対応']),
            'を行って下さい。'
          ]
        });
      } else {
        errors.push({
          level: 'good',
          message: 'サービスの選択に進むことができます。'
        });
      }
      return errors;
    }
  },
  methods: {
    ...mapActions({
      _gqlQuery: 'hds/gqlQuery',
    }),
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    async load() {
      try {
        this.selfDataProfilesLoading = true;

        // selfDataProfiles を生成する
        const dcmIds = new BetterSet();
        const {data: dataProfiles} = await this.hcsClient.get('/data_profiles');
        for(const dataProfile of dataProfiles) {
          dcmIds.add(dataProfile.dataImplementation.dataComponentModelId);
          dcmIds.add(dataProfile.dataComponentModelId);
        }
        const {dataComponentModels} = await this._gqlQuery({
          query: gql`
                    query FindDataComponentModel(
                        $dataComponentModelIds: [ID!]!,
                    ) {
                        dataComponentModels: dataComponentModelInBulk(dataComponentModelIds: $dataComponentModelIds) {
                            id
                            name
                            description
                            dictionary {
                                id
                                name
                                isDeleted
                            }
                        }
                    }`,
          variables: {
            dataComponentModelIds: dcmIds.asArray()
          }
        });
        this.selfDataProfiles = dataProfiles.map(dp => {
          const di = dp.dataImplementation;
          const commonDcm = dataComponentModels.find(dcm => dcm.id === dp.dataComponentModelId);
          const localDcm = dataComponentModels.find(dcm => dcm.id === di.dataComponentModelId);
          return {
            ...dp,
            commonDataComponentModel: commonDcm,
            localDataComponentModel: localDcm,
            name: localDcm?.name,
            description: localDcm?.description,
            controllerIds: [di.controllerId],
          };
        });

        // コントローラーのリストを生成
        const {data: controllers} = await this.hcsClient.get('/controllers');
        const controllerMap = new Map();
        for(const dataProfile of dataProfiles) {
          const controllerId = dataProfile.dataImplementation.controllerId;
          if(!controllerMap.has(controllerId)) {
            const controller = controllers.find(c => c.id === controllerId);
            controllerMap.set(controller.id, controller);
          }
        }
        this.controllers = Array.from(controllerMap.values());
      } finally {
        this.selfDataProfilesLoading = false;
      }
    },
    getLinkBySelf(selfDataPropertyProfileId, defaultValue) {
      const links = this.linkManager.get({selfDataPropertyProfileId});
      if(links.length === 0) {
        return defaultValue;
      }
      return links[0];
    },
    setLink(selfDataPropertyProfileId, otherDataPropertyProfileId) {
      this.linkManager.removeBy({selfDataPropertyProfileId});
      if(!otherDataPropertyProfileId) {
        return;
      }

      this.linkManager.add({selfDataPropertyProfileId, otherDataPropertyProfileId});
      this.tradeContract.markDirty();
    },
    clearLink() {
      this.linkManager.clear();
    },
    resetServiceProfile() {
      this.tradeContract.selfServiceProfileId = null;
      this.tradeContract.selfEventProfileIds = [];
    },
    otherDataPropertyProfileName(dpd) {
      return this.otherDataPropertyProfileNameIs === 'common' ? dpd.commonName : dpd.localName;
    },
    otherDataPropertyProfileDescription(dpd) {
      return this.otherDataPropertyProfileNameIs === 'common' ? dpd.commonDescription : dpd.localDescription;
    },
    dictionaryTypeName(dictionaryType) {
      return DICTIONARY_TYPE_NAMES[dictionaryType];
    },
    dictionaryTypeColor(dictionaryType) {
      return DICTIONARY_TYPE_COLORS[dictionaryType];
    },
    selfDataProfileIntoView() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.self-data-profile');
        scrollIntoView(el, {
          scrollMode: 'if-needed',
          behavior: 'smooth'
        });
      });
    },
    /**
     * データ項目プロファイル名が同じもの同士で自動マッピングを行う
     */
    async autoMapping() {
      const hasMapping = this.selfDataPropertyProfiles
        .map(selfDpp => this.getLinkBySelf(selfDpp.id, null))
        .some(dppLink => dppLink !== null);
      const {value: confirm} = await Swal.fire({
        title: '自動マッピング',
        html: [
          'データ項目プロファイル名が同じもの同士で自動マッピングを行います。',
          hasMapping ? '既存のマッピングは上書きされる可能性があります。' : '',
          '続行しますか？'
        ].filter(s => !!s).join('<br>'),
        icon: 'info',
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ",
      });
      if (!confirm) {
        return;
      }

      let mappingCount = 0;
      for(const selfDataPropertyProfile of this.selfDataPropertyProfiles) {
        const otherDataPropertyProfile = this.otherDataPropertyProfiles.find(dpp =>
            dpp.commonName === selfDataPropertyProfile.name ||
            dpp.localName === selfDataPropertyProfile.name);
        if(otherDataPropertyProfile) {
          mappingCount++;
          this.setLink(selfDataPropertyProfile.id, otherDataPropertyProfile.id);
        }
      }

      if(mappingCount) {
        this.showSnackbar({
          message: `${mappingCount} 件のマッピングを行いました`,
        });
      } else {
        this.showSnackbar({
          color: 'warn',
          message: '何もマッピングされませんでした',
        });
      }
    }
  },
  watch: {
    'tradeContract': {
      immediate: true,
      async handler(tradeContract) {
        this.linkManager.dataPropertyProfileMaps = tradeContract.dataPropertyProfileMaps;
      }
    },
    'tradeContract.otherDataProfileId': {
      immediate: true,
      async handler(otherDataProfileId) {
        this.otherDataPropertyProfiles.forEach(dpp => {
          dpp.connector.unregister();
        });

        if(otherDataProfileId) {
          this.otherDataPropertyProfilesLoading = true;
          this.otherDataProfileLoading = true;

          // otherDataProfile の生成
          try {
            const dataProfile = await this.loadAssetsDataProfile(otherDataProfileId);
            this.otherDataProfile = {
              ...dataProfile,
              name: dataProfile.commonDataComponentModel?.name,
              description: dataProfile.commonDataComponentModel?.description,
              parameterDescription: dataProfile.dataImplementation?.parameterDescription
            };
          } finally {
            this.otherDataProfileLoading = false;
          }

          try {
            // otherDataPropertyProfiles の生成
            this.otherDataPropertyProfiles = (await this.loadAssetsDataPropertyProfiles(otherDataProfileId))
                .map(dpp => {
                  const dpi = dpp.dataPropertyImplementation;
                  const commonDpd = dpp.commonDataPropertyDefinition;
                  const localDpd = dpp.localDataPropertyDefinition;
                  return {
                    ...dpp,
                    dataPropertyImplementation: dpi,
                    index: dpi.index,
                    defaultValue: dpi.defaultValue,
                    commonName: commonDpd?.name,
                    commonDescription: commonDpd?.description,
                    localName: localDpd?.name,
                    localDescription: localDpd?.description,
                    dataType: localDpd?.dataType ?? 'string',
                    isPrimaryKey: localDpd?.isPrimaryKey ?? false,
                    isRequired: localDpd?.isRequired ?? false,
                  };
                })
                .map(dpp => ({
                  ...dpp,
                  connector: new DataPropertyProfileConnector({
                    serviceType: this.tradeContract.otherServiceType,
                    dataPropertyProfileId: () => dpp.id,
                    defaultValue: () => dpp.defaultValue,
                    isRequired: () => dpp.isRequired,
                    dataType: () => dpp.dataType,
                  }).register(this.linkManager)
                }))
                .sort((a, b) => sortFn(a.index, b.index));
            this.linkManager.dataPropertyProfileMaps = this.tradeContract.dataPropertyProfileMaps;
          } finally {
            this.otherDataPropertyProfilesLoading = false;
          }
        } else {
          this.otherDataProfile = {};
          this.otherDataPropertyProfiles = [];
        }

        this.$emit('loaded-other-data-profile');
      }
    },
    'tradeContract.selfDataProfileId': {
      immediate: true,
      async handler(selfDataProfileId) {
        this.selfDataPropertyProfiles.forEach(dpp => {
          dpp.connector.unregister();
        });

        if(selfDataProfileId) {
          try {
            this.selfDataPropertyProfilesLoading = true;

            this.selfDataPropertyProfiles = (await this.loadDataPropertyProfiles(selfDataProfileId))
                .map(dpp => {
                  const dpi = dpp.dataPropertyImplementation;
                  const localDpd = dpp.localDataPropertyDefinition;
                  return {
                    ...dpp,
                    index: dpi.index,
                    defaultValue: dpi.defaultValue,
                    name: localDpd?.name,
                    description: localDpd?.description,
                    dataType: localDpd?.dataType ?? 'string',
                    isPrimaryKey: localDpd?.isPrimaryKey ?? false,
                    isRequired: localDpd?.isRequired ?? false,
                  };
                })
                .map(dpp => ({
                  ...dpp,
                  connector: new DataPropertyProfileConnector({
                    serviceType: this.tradeContract.serviceType,
                    dataPropertyProfileId: () => dpp.id,
                    defaultValue: () => dpp.defaultValue,
                    isRequired: () => dpp.isRequired,
                    dataType: () => dpp.dataType,
                  }).register(this.linkManager)
                }))
                .sort((a, b) => sortFn(a.index, b.index));
            this.linkManager.dataPropertyProfileMaps = this.tradeContract.dataPropertyProfileMaps;
          } finally {
            this.selfDataPropertyProfilesLoading = false;
          }
        } else {
          this.selfDataPropertyProfiles = [];
          this.linkManager.dataPropertyProfileMaps = [];
        }

        this.$emit('loaded-self-data-profile');
      }
    },
    'linkManager.dataPropertyProfileMaps': {
      handler(dataPropertyProfileMaps) {
        this.tradeContract.dataPropertyProfileMaps = dataPropertyProfileMaps;

        // データマッピングの妥当性検査
        // src/components/pages/contract/TradeContract/AdaptContract/index.vue の load メソッドも参照
        let dataPropertyProfileMapsIsReady = this.linkManager.hasLink;
        for(const conn of this.linkManager.getConnectors()) {
          const errors = conn.errors();
          if(errors.hasError) {
            dataPropertyProfileMapsIsReady = false;
            break;
          }
        }
        this.tradeContract.dataPropertyProfileMapsIsReady = dataPropertyProfileMapsIsReady;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .el-table tr.highlight-row {
    background-color: #ffa3a3;
    td {
      mix-blend-mode: multiply;
    }
  }

  .el-table {
    th.mapping {
      padding: 6px 0;
    }

    .cell.mapping {
      white-space: pre-wrap;
      position: relative;

      .subtitle {
        font-size: 80%;
        line-height: 1;
      }

      .v-btn {
        position: absolute;
        right: 5px;
        top: 50%;
        margin-top: -18px;
      }
    }
  }
}

.data-profile-select-item {
  max-width: 800px;
  min-width: 500px;
}

.col.name {
  max-width: 200px;
}
</style>
